import { claimDetailFooterSlot, provideDetailFooterSlot } from '@modules/common/components/detail-breadcrumbs/detail-footer-slot';
import { APP_CRUMBS, TREE_CRUMBS, UNTITLED_CRUMB, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { Component, inject, Injector, Input, OnInit } from '@angular/core';
import { AlertService } from '@modules/alert/services/alert.service';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { type ContainerDndConfig, ContainerDndService } from '@modules/common/services/container-dnd.service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { Tab } from '@modules/common/types/tab';
import { type DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import { SheetFoldersService } from '@modules/sheets/services/sheet-folders.service';
import { SheetsListStateService } from '@modules/sheets/services/sheets-list-state.service';
import { SheetsService } from '@modules/sheets/services/sheets.service';
import { SheetsAppStateService } from '@modules/sheets/services/state.service';
import { openWorkbookInEditor } from '@modules/sheets/open-workbook';
import { type Sheet } from '@modules/sheets/types/sheet';
import { Router } from '@angular/router';
import { SheetFolder, type SheetFolderFormGroupConfig } from '@modules/sheets/types/sheet-folder';
import { SheetsFilters } from '@modules/sheets/types/sheets-filters';
import { SheetsListState } from '@modules/sheets/types/sheets-list-state';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import { StateKey } from '@modules/settings/types/state-key';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { combineLatest } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

declare module '@modules/settings/types/state' {
  export interface State {
    [StateKey.sheetsFFListState]?: SheetsListState;
  }
}

@Component({
  selector: 'app-sheet-folder-form',
  templateUrl: './sheet-folder-form.component.html',
  styleUrls: ['./sheet-folder-form.component.less'],
  standalone: false,
  providers: [
    ...SheetsListStateService.providers({
      stateKey: StateKey.sheetsFFListState,
    }),
    provideDetailFooterSlot(),
  ],
})
export class SheetFolderFormComponent
  extends FullFormBaseComponent<StitchType.SHEET_FOLDER, SheetFolder, SheetFolderFormGroupConfig>
  implements OnInit
{
  /** NFR-8: the header buttons' names, from their one definition. */
  protected readonly S = SHEETS_STRINGS;
  public changesKey = 'sheet-folder';
  tabs: Tab[] = [
    { title: 'Workbooks', value: 'sheetFolder' },
    { title: 'Details', value: 'details' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  protected override attachmentsMenuStateVariant = 'sheetFolders';
  public parentSuggestions: AutocompleteFactory<SheetFolder>;
  public sheetsFilters: SheetsFilters;

  protected dragDataType = DragDataTypes.sheetFolder;
  protected override childItemDragType = DragDataTypes.sheet;
  protected override childItemContainerIdField = 'sheetFolderId';

  @Input() sheetFolder = new SheetFolder();

  /** stitch-app-shell breadcrumbs, as on every detail page. */
  protected readonly navigation = new DetailNavigation(SheetsAppStateService, { appLabel: APP_CRUMBS.sheets });
  /** The item list's pager joins the breadcrumbs row in the main view. */
  private readonly footerSlot = claimDetailFooterSlot(this.navigation.enabled);

  /** [workspace ›] Sheets app › Folders › this sheetFolder. */
  protected breadcrumbs(): DetailCrumb[] {
    return this.navigation.crumbs({
      view: TREE_CRUMBS.folders,
      fallbackView: TREE_CRUMBS.folders,
      itemTitle: this.stitchItem?.title || UNTITLED_CRUMB,
    });
  }

  private readonly sheetsListStateService = inject(SheetsListStateService, { self: true });
  private readonly containerDndService = inject(ContainerDndService);
  private readonly alertService = inject(AlertService);
  private readonly appStateService = inject(BaseAppStateService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  private readonly router = inject(Router);
  /** Sheets' app state, inside Sheets only (`*appSheetsAppContext`): where "Open" leaves the workbook selected. */
  private readonly sheetsAppState = inject(SheetsAppStateService, { optional: true });

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  private dndConfig(): ContainerDndConfig {
    return {
      containerType: DragDataTypes.sheetFolder,
      childItemType: DragDataTypes.sheet,
      childService: this.sheetsService,
      containerIdField: 'sheetFolderId',
      appStateService: this.appStateService,
      spaceId: this.spaceContextStore.contextId(),
    };
  }

  constructor(
    private sheetFoldersService: SheetFoldersService,
    private sheetsService: SheetsService,
    injector: Injector,
  ) {
    super(injector, sheetFoldersService);
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    super.ngOnInit();

    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      this.parentSuggestions = this.sheetFoldersService.getAutocompleteSuggestions({ exceptIds: [this.stitchItem.id] });
    });

    this.stitchItemChanged
      .pipe(
        switchMap(() => combineLatest([this.sheetsListStateService.value(), this.displayTimeZone$])),
        takeUntil(this.alive),
      )
      .subscribe(([, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState reads it to reckon its day windows in the account
        // zone (SBUX-829).
        this.sheetsFilters = new SheetsFilters({ containersIds: [this.stitchItem.id], displayTimeZone });
        this.sheetsFilters.applyListState(this.sheetsListStateService.state);
      });
  }

  /**
   * Actions
   */

  dndDrop(dragData: DragData): void {
    this.containerDndService.handleDndDrop(
      this.stitchItem,
      dragData,
      this.dndConfig(),
      this.stitchService,
      this.alertService,
    );
  }

  /**
   * "Open" in the "…" menu of a workbook card on the folder's Workbooks tab opens it in the editor, as the
   * prototype's does everywhere (`open:()=>openItem(i,true)`), and as the list's cards do: selected first, so the
   * editor's header names it at once and closing the editor returns to it. In a dialog, the dialog closes first, as
   * "Edit workbook" in a workbook's own dialog does. A click on the card still previews it in the Dynamic Panel.
   */
  openWorkbook(sheet: Sheet): void {
    if (!sheet?.id) return;
    if (this.showCloseButton === false) this.handleClose();
    if (!this.dp) this.sheetsAppState?.setMainView(sheet);
    void openWorkbookInEditor(this.router, sheet.id);
  }

  /**
   * Helpers
   */

  get stitchItem(): SheetFolder {
    return this.sheetFolder;
  }

  protected shouldRefreshList(prev, current) {
    return SheetFolder.shouldRefreshList(prev, current);
  }

  protected fromFormGroup(): SheetFolder {
    return SheetFolder.fromFormGroup(this.form);
  }

  protected asFormGroup() {
    return this.sheetFolder.asFormGroup({
      sheetFoldersService: this.sheetFoldersService,
      maxAttachmentSize: this.maxUploadSize(),
    });
  }
}
